import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppHeader, AppLogout, AppNavigation, AppSidebar, Nav, NavLink, Text } from '@ui'

import { icons } from '../../features/icons'
import { getCurrentMerchantID } from '../../features/data'
import { usePermissions } from '../../hooks'
import { cssVariables } from '../rewards-ui/styles'
import { theme } from '../../constants/environment'

import { PluxeeLogoUrl } from '../rewards-ui/components/Icons/PluxeeLogo'
import { useCurrentMerchantUser } from '../../hooks'
import { get } from 'lodash'

import { useHasOpenInvites } from '../../hooks'

const Wrapper = styled.div`
	display: block;

	& .AppHeader-logo {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	& .AppHeader-svg {
		transition: none;
		transform: none;
		position: static;
	}

	.AppHeader--bottom {
		border: 0;
	}

	.AppNavigation .Nav-link,
	.AppLogout-text {
		font-weight: 500;
	}

	@media (max-width: 1023px) {
		.AppHeader > button {
			min-height: 44px;
		}

		.AppHeader > button img,
		.AppHeader > button span svg {
			max-width: 42px;
			max-height: 42px;
		}

		.AppNavigation .Nav-link,
		.AppLogout-text {
			display: none;
		}
	}

	@media (max-width: 767px) {
		.AppNavigation {
			width: 100%;
		}
	}
`

const StyledAppSidebar = styled(AppSidebar)`
	border: 0;

	@media (max-width: 767px) {
		justify-content: flex-start;
	}
`
const LinkStyled = styled(NavLink)`
	.Nav-link:hover {
		color: ${cssVariables.color.brand.secondary};
	}
	.Nav-link.is-selected {
		color: ${cssVariables.color.brand.secondary};
	}
`
const TopBorderedNavLinkStyled = styled(NavLink)`
	border-top: 1px solid #e4e4e4;

	.Nav-link:hover {
		color: ${cssVariables.color.brand.secondary};
	}
	.Nav-link.is-selected {
		color: ${cssVariables.color.brand.secondary};
	}
`

const StyledText = styled(Text)`
	font-size: 8px;
	margin-bottom: 8px;
	margin-left: 15px;
	padding-right: 15px;
	text-align: center;
	width: calc(100% - 30px);
	line-height: 1.4;
	overflow: hidden;
	overflow-wrap: anywhere;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;

	@media (max-width: 1023px) {
		display: none;
	}
`

const LabelWithNotification = styled.span`
	position: relative;
	display: inline-block;
`

const RedDot = styled.span`
	position: absolute;
	top: 0;
	right: 0;
	width: 8px;
	height: 8px;
	background-color: red;
	border-radius: 50%;
	transform: translate(100%, -30%);
`

export const SideNav = () => {
	const { t: translate } = useTranslation()
	const navigate = useNavigate()
	const { pathname: path } = useLocation()
	const { currentMerchantID } = getCurrentMerchantID()

	const goTo = nextPath => () => navigate(nextPath)

	const {
		canSignContract,
		canControlFeatures,
		canManageMarketCompetitors,
		canManageAdmins,
		canManageCollections,
		canViewAllInvoiceRequest,
		canCreateMerchant,
	} = usePermissions()

	const { data: userData, error: userError } = useCurrentMerchantUser()
	const email = get(userData, 'merchantMe.email.email')

	const { hasOpenInvites, error, loading } = useHasOpenInvites()

	const showRedDot = !error && !loading && hasOpenInvites

	const innerNavbuttons = (
		<AppNavigation>
			<Nav vertical>
				<LinkStyled
					label={translate('navbar.home')}
					icon="home"
					onClick={goTo('/')}
					selected={path === '/'}
					solid={path === '/'}
					sm
					data-test-id="homeNav"
				/>
				<LinkStyled
					label={translate('navbar.profile')}
					icon="merchant"
					onClick={goTo('/brand-profile')}
					selected={path.startsWith('/brand-profile')}
					solid={path.startsWith('/brand-profile')}
					sm
					data-test-id="profileNav"
				/>
				<LinkStyled
					label={translate('navbar.campaigns')}
					icon="campaigns"
					onClick={goTo('/campaigns')}
					selected={path.startsWith('/campaigns')}
					solid={path.startsWith('/campaigns')}
					sm
					data-test-id="campaignsNav"
				/>
				<LinkStyled
					label={translate('navbar.insights')}
					icon="reports"
					onClick={goTo('/insights')}
					selected={path.startsWith('/insights')}
					solid={path.startsWith('/insights')}
					sm
					data-test-id="insightsNav"
				/>
				<LinkStyled
					label={translate('navbar.payments')}
					icon="card"
					onClick={goTo('/payments')}
					selected={path.startsWith('/payments')}
					solid={path.startsWith('/payments')}
					sm
					data-test-id="paymentsNav"
				/>
				{canControlFeatures && currentMerchantID && (
					<LinkStyled
						forwardedAs="a"
						type={undefined}
						href={`https://kachadmin.demo.kachingcashback.com/accounts/${encodeURIComponent(currentMerchantID)}`}
						target="_blank"
						rel="noopener noreferrer"
						style={{ textDecoration: 'none' }}
						label={translate('navbar.accountBudgets')}
						icon="cashback"
						sm
						data-test-id="accountBudgetsNav"
					/>
				)}
				{/* TODO */}
				{/* Navigation link should be hidden when the visible property value is undefined */}
				<LinkStyled
					label={translate('navbar.settings')}
					icon="rule"
					onClick={goTo('/settings')}
					selected={path.startsWith('/settings')}
					solid={path.startsWith('/settings')}
					sm
					visible={canSignContract || canControlFeatures || canManageMarketCompetitors || canManageAdmins}
					data-test-id="settingsNav"
				/>
				<LinkStyled
					label={translate('navbar.finance')}
					icon="cashback"
					onClick={goTo('/finance')}
					selected={path.startsWith('/finance')}
					solid={path.startsWith('/finance')}
					sm
					visible={canViewAllInvoiceRequest}
					data-test-id="financeNav"
				/>

				<LinkStyled
					label="System Settings"
					icon="rule"
					onClick={goTo('/system-settings')}
					selected={path.startsWith('/system-settings')}
					solid={path.startsWith('/system-settings')}
					sm
					visible={canManageCollections}
					data-test-id="systemSettingsNav"
				/>
			</Nav>
		</AppNavigation>
	)
	return (
		<Wrapper>
			<StyledAppSidebar>
				{theme === 'pluxee' && (
					<AppHeader imageUrl={PluxeeLogoUrl} imageHeight={50} imageWidth={50} vertical onClick={goTo('/')} sidebar>
						{innerNavbuttons}
					</AppHeader>
				)}
				{theme !== 'pluxee' && (
					<AppHeader
						imageSvg={icons.kachingLogo.svgMarkup}
						imageHeight={50}
						imageWidth={100}
						vertical
						onClick={goTo('/')}
						sidebar
					>
						{innerNavbuttons}
					</AppHeader>
				)}
				<AppHeader fixed vertical bottom>
					<AppNavigation>
						<Nav vertical>
							<LinkStyled
								label={translate('navbar.advancedSettings')}
								icon="rule"
								onClick={goTo('/advanced-settings')}
								selected={path.startsWith('/advanced-settings')}
								solid={path.startsWith('/advanced-settings')}
								sm
								bottom
								visible={canCreateMerchant}
								data-test-id="advancedSettingsNav"
							/>
							<LinkStyled
								label={
									<LabelWithNotification>
										{translate('navbar.userAccount')}
										{showRedDot && <RedDot />}
									</LabelWithNotification>
								}
								icon="user"
								onClick={goTo('/user-account')}
								selected={path.startsWith('/user-account')}
								solid={path.startsWith('/user-account')}
								sm
								bottom
								data-test-id="userAccountNav"
							/>
						</Nav>
					</AppNavigation>
					<AppLogout logoutText={translate('navbar.logoutButton')} onClick={goTo('/logout')} data-test-id="logout" />
					<StyledText>{translate('navbar.signedInAs', { email: email })}</StyledText>
				</AppHeader>
			</StyledAppSidebar>
		</Wrapper>
	)
}
